import React, { useState, useEffect, useRef } from "react";
import { FaUpload, FaFileExport, FaArrowUp, FaArrowDown, FaCopy } from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import { jsonrepair } from "jsonrepair";
import jsBeautify from "js-beautify";

const JSONSchemaGenerator = () => {
  const [jsonInput, setJsonInput] = useState("");
  const [jsonSchema, setJsonSchema] = useState("");
  const [validationResult, setValidationResult] = useState("");
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const textareaRef = useRef(null);
  const outputRef = useRef(null);

  const exampleJSON = `{
    "company": "SFDC247",
    "employees": [
      {
        "id": 1,
        "name": "John Doe",
        "role": "Salesforce Developer",
        "skills": ["Apex", "Visualforce", "LWC", "JavaScript"],
        "details": {
          "years_of_experience": 5,
          "certifications": ["Admin", "Platform Developer I", "Platform Developer II"]
        }
      },
      {
        "id": 2,
        "name": "Jane Smith",
        "role": "Salesforce Architect",
        "skills": ["Integration", "API Design", "Data Modeling"],
        "details": {
          "years_of_experience": 10,
          "certifications": ["System Architect", "Application Architect"]
        }
      }
    ]
  }`;

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileContent = e.target.result;
        setJsonInput(fileContent);
        setUploading(false);
        event.target.value = "";
      };
      reader.onerror = () => {
        setError("Error reading the file. Please try again.");
        setUploading(false);
        event.target.value = "";
      };
      reader.readAsText(file);
    }
  };

  const generateSchema = () => {
    setProcessing(true);
    setTimeout(() => {
      try {
        const jsonObject = JSON.parse(jsonInput);
        const schema = generateJSONSchema(jsonObject);
        setJsonSchema(jsBeautify(JSON.stringify(schema), { indent_size: 2 }));
        setValidationResult("✅ Schema Generated Successfully");
        setError("");
      } catch (err) {
        setJsonSchema("");
        setError("Invalid JSON. Please check your input.");
        setValidationResult("❌ Failed to Generate Schema");
      } finally {
        setProcessing(false);
      }
    }, 500);
  };

  const generateJSONSchema = (data) => {
    if (Array.isArray(data)) {
      return {
        type: "array",
        items: generateJSONSchema(data[0] || {}),
      };
    } else if (typeof data === "object" && data !== null) {
      const properties = {};
      for (const key in data) {
        properties[key] = generateJSONSchema(data[key]);
      }
      return {
        type: "object",
        properties,
        required: Object.keys(data),
      };
    } else {
      return { type: typeof data };
    }
  };

  const fixJSON = () => {
    setProcessing(true);
    setTimeout(() => {
      try {
        const repairedJSON = jsonrepair(jsonInput);
        setJsonInput(jsBeautify(repairedJSON, { indent_size: 2 }));
        setError("");
        setValidationResult("✅ JSON Fixed Successfully");
      } catch (err) {
        setError("Unable to fix JSON. Please check for invalid syntax.");
        setValidationResult("❌ Fix Failed");
      } finally {
        setProcessing(false);
      }
    }, 500);
  };

  const scrollToTop = () => {
    if (outputRef.current) {
      outputRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const scrollToBottom = () => {
    if (outputRef.current) {
      outputRef.current.scrollTo({ top: outputRef.current.scrollHeight, behavior: "smooth" });
    }
  };

  const handleExport = () => {
    const blob = new Blob([jsonSchema], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "generated-schema.json";
    link.click();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonSchema);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const clearInputs = () => {
    setJsonInput("");
    setJsonSchema("");
    setValidationResult("");
    setError("");
  };

  const useExampleData = () => {
    setJsonInput(exampleJSON);
    setJsonSchema("");
    setValidationResult("");
    setError("");
  };

  const renderInputWithLineNumbers = () => (
    <div style={{ display: "flex", position: "relative", height: "300px" }}>
      <div
        style={{
          overflowY: "auto",
          backgroundColor: "#f8f9fa",
          borderRight: "1px solid #ccc",
          textAlign: "right",
          color: "#6c757d",
          padding: "5px",
          width: "40px",
        }}
      >
        {jsonInput.split("\n").map((_, index) => (
          <div key={index} style={{ height: "1.5em", lineHeight: "1.5em" }}>
            {index + 1}
          </div>
        ))}
      </div>
      <textarea
        ref={textareaRef}
        className="form-control"
        value={jsonInput}
        onChange={(e) => setJsonInput(e.target.value)}
        placeholder="Enter JSON object or array here..."
        style={{
          flex: "1",
          height: "100%",
          resize: "none",
          border: "2px solid #ccc",
          fontFamily: "monospace",
        }}
      />
      <label
        htmlFor="upload-file"
        style={{ position: "absolute", top: "10px", right: "10px", cursor: "pointer" }}
      >
        <FaUpload size={20} />
      </label>
      <input
        type="file"
        id="upload-file"
        style={{ display: "none" }}
        accept=".json"
        onChange={handleFileUpload}
      />
    </div>
  );

  const renderOutputWithoutLineNumbers = () => (
    <pre
      ref={outputRef}
      style={{
        height: "300px",
        overflowY: "auto",
        border: "2px solid #ccc",
        padding: "10px",
        backgroundColor: "#f8f9fa",
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        margin: 0,
      }}
    >
      {jsonSchema}
    </pre>
  );

  return (
    <div className="container mt-4">
      <div className="text-center mb-3">
        <button className="btn btn-info" onClick={useExampleData}>
          Use Example Data
        </button>
      </div>
      {uploading && <div className="text-center mb-3"><ClipLoader size={30} color="#007bff" /></div>}
      <div className="row">
        <div className="col-md-12">{renderInputWithLineNumbers()}</div>
      </div>
      <div className="text-center mt-3">
        <button className="btn btn-success me-2" onClick={generateSchema} disabled={processing}>
          {processing ? <ClipLoader size={15} color="white" /> : "Generate Schema"}
        </button>
        <button className="btn btn-warning me-2" onClick={fixJSON}>
          Fix JSON
        </button>
        <button className="btn btn-secondary me-2" onClick={clearInputs}>
          Clear
        </button>
      </div>
      {validationResult && (
        <div className="text-center mt-3">
          <strong>{validationResult}</strong>
        </div>
      )}
      {jsonSchema && (
        <div className="mt-4">
          <h5>Generated JSON Schema</h5>
          {renderOutputWithoutLineNumbers()}
          <div className="text-center mt-3">
            <button className="btn btn-secondary me-2" onClick={scrollToTop}>
              <FaArrowUp /> Scroll to Top
            </button>
            <button className="btn btn-secondary me-2" onClick={scrollToBottom}>
              <FaArrowDown /> Scroll to Bottom
            </button>
            <button
              className={`btn ${copySuccess ? "btn-success" : "btn-primary"} me-2`}
              onClick={handleCopy}
            >
              {copySuccess ? "Copied!" : <FaCopy />} Copy Output
            </button>
            <button className="btn btn-warning" onClick={handleExport}>
              <FaFileExport /> Export Schema
            </button>
          </div>
        </div>
      )}
      {error && <div className="alert alert-danger mt-3">{error}</div>}
    </div>
  );
};

export default JSONSchemaGenerator;